import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { QuizWho } from "./types";

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const MONTHS = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i, 1).toLocaleDateString("en", { month: "short" })
);
const currentYear = new Date().getFullYear();
const YEARS = Array.from(
  { length: currentYear - 1930 + 1 },
  (_, i) => currentYear - i
);

export interface QuizStepDateOfBirthProps {
  who: QuizWho | "";
  day: string;
  month: string;
  year: string;
  onDayChange: (d: string) => void;
  onMonthChange: (m: string) => void;
  onYearChange: (y: string) => void;
  error: string | null;
}

const titleYour = "What is your date of birth?";
const titleOther = "What is their date of birth?";
const captionChild =
  "Rest assured that our formulations are specifically developed for children and teens, helping them to navigate the pressures of today's society.";

export const QuizStepDateOfBirth: React.FC<QuizStepDateOfBirthProps> = ({
  who,
  day,
  month,
  year,
  onDayChange,
  onMonthChange,
  onYearChange,
  error,
}) => {
  const title = who === "your" ? titleYour : titleOther;

  return (
    <motion.li
      className="step"
      data-step="date_of_birth"
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <div className="quiz-inner">
        <div className="quiz-title" data-question-type="title">
          <h2>{title}</h2>
        </div>

        {who === "child" && (
          <div className="quiz-caption" data-question-type="caption">
            <p>{captionChild}</p>
          </div>
        )}

        <div data-question-type="input-date">
          <select
            id="quiz-step-date-day"
            name="quiz-step-date-day"
            value={day}
            autoFocus
            onChange={(e) => onDayChange(e.target.value)}
            aria-label="Day"
          >
            <option value="">Day</option>
            {DAYS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <select
            id="quiz-step-date-month"
            name="quiz-step-date-month"
            value={month}
            onChange={(e) => onMonthChange(e.target.value)}
            aria-label="Month"
          >
            <option value="">Month</option>
            {MONTHS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <select
            id="quiz-step-date-year"
            name="quiz-step-date-year"
            value={year}
            onChange={(e) => onYearChange(e.target.value)}
            aria-label="Year"
          >
            <option value="">Year</option>
            {YEARS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              key={error}
              className="quiz-error"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.li>
  );
};
